import {RootState} from 'store'
import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit'
import {fetchCountryList} from 'api/commonApi'
import {
  getInvoiceTargetList,
  GetInvoiceTargetListRequest,
  InvoiceTarget,
} from 'api/productApi'
import {
  fromIsoDateOrStringToLocalDate,
  getDefaultPeriodString,
  toParsedDate,
} from 'helpers/dateHelper'
import {zeroPad} from 'helpers/commonHelper'
import {getListOrgDetailApi} from 'api/salesApi'

export interface DetailedInvoiceTarget extends InvoiceTarget {
  orgName?: string
  orgUserFirstname?: string
  orgUserLastname?: string
  email?: string
  countryName?: string
  billingFileId?: number
}

const formatDate = (date: string) => {
  if (!date) return ''
  const localDate = fromIsoDateOrStringToLocalDate(date)
  const {years, months} = toParsedDate(localDate)
  return `${years}${zeroPad(months)}`
}

export const searchCustomerRegistration = createAsyncThunk(
  'api/eeg/CustomerRegistration/search',
  async (payload: GetInvoiceTargetListRequest, {rejectWithValue}) => {
    try {
      const responseSearch = await getInvoiceTargetList({
        from: formatDate(payload.from),
        to: formatDate(payload.to),
        page: payload.page,
        size: payload.size,
        region: payload.region,
        orgName: payload.orgName || '',
      })
      let targets: DetailedInvoiceTarget[] = []
      if (
        responseSearch.data.content &&
        responseSearch.data.content.length > 0
      ) {
        const orgIds = responseSearch.data.content.map((item) =>
          Number(item.orgId),
        )
        const responseOrgsDetail = await getListOrgDetailApi({orgId: orgIds})
        const responseCountries = await fetchCountryList()

        targets = responseSearch.data.content.map((invoice) => {
          const infoOrg = responseOrgsDetail?.list.find(
            (item) => item.org?.id === Number(invoice.orgId),
          )
          const countryName = responseCountries.list.find(
            (item) => item.code === infoOrg?.org?.countryCode,
          )?.name

          return {
            ...invoice,
            orgId: infoOrg?.org?.id?.toString() || '',
            orgName: infoOrg?.org?.name,
            email: infoOrg?.chiefUser?.email,
            orgUserFirstname: infoOrg?.chiefUser?.firstName,
            orgUserLastname: infoOrg?.chiefUser?.lastName,
            countryName,
          }
        })
      }
      responseSearch.data.content = targets
      return responseSearch
    } catch (err) {
      return rejectWithValue(err.message)
    }
  },
)

interface CustomerRegistrationState {
  loading: boolean
  error: any
  items: DetailedInvoiceTarget[]
  period: PeriodString
  search: CustomerSearchKind
  paging: PagingV3
  oid?: string | null
  region?: string
  isConfirmed?: boolean | null
  pagingInfo: {
    totalPages: number
    totalElements: number
  }
  normDialogOpen: boolean
  selected: DetailedInvoiceTarget[]
  reSelectYn: YN | undefined
  reAnalysisYn: YN | undefined
  isIcaSelectOpen: boolean
  isIcaConfirmOpen: boolean
  isIcaResultOpen: boolean
  sraYn?: YN | undefined
}

const initialState: CustomerRegistrationState = {
  loading: false,
  error: null,
  items: [],
  period: getDefaultPeriodString(),
  search: {orgName: ''},
  paging: {pageNumber: 0, pageSize: 10},
  pagingInfo: {
    totalPages: 1,
    totalElements: 0,
  },
  region: '',
  isConfirmed: null,
  normDialogOpen: false,
  selected: [],
  reSelectYn: undefined,
  reAnalysisYn: undefined,
  isIcaSelectOpen: false,
  isIcaConfirmOpen: false,
  isIcaResultOpen: false,
  oid: '',
  sraYn: undefined,
}

export const customerSlice = createSlice({
  name: 'customerRegistrationSlice',
  initialState,
  reducers: {
    setIsConfirmed(state, action: PayloadAction<boolean | null>) {
      state.isConfirmed = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(searchCustomerRegistration.pending, (state, action) => {
      state.loading = true
      state.error = null
      const {from, to, orgName, page, size} = action.meta.arg
      state.period = {startDate: from, endDate: to}
      state.search = {orgName: orgName || ''}
      state.paging = {pageNumber: page, pageSize: size}
    })
    builder.addCase(searchCustomerRegistration.fulfilled, (state, action) => {
      state.loading = false

      const {
        content: items,
        number: page,
        size,
        totalPages,
        totalElements,
      } = action.payload.data

      state.items = items
      state.loading = false
      state.error = null
      state.paging.pageNumber = page
      state.paging.pageSize = size
      state.pagingInfo.totalPages = totalPages
      state.pagingInfo.totalElements = totalElements
    })
    builder.addCase(searchCustomerRegistration.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })
  },
})

export default customerSlice.reducer

export const selectList = (state: RootState) => state.invoiceList.items
export const selectLoading = (state: RootState) => state.invoiceList.loading
export const selectError = (state: RootState) => state.invoiceList.error
export const selectSearch = (state: RootState) => state.invoiceList.search
export const selectPeriod = (state: RootState) => state.invoiceList.period
export const selectPaging = (state: RootState) => state.invoiceList.paging
export const selectPagingInfo = (state: RootState) =>
  state.invoiceList.pagingInfo
export const selectSelected = (state: RootState) => state.invoiceList.selected
export const selectRegion = (state: RootState) => state.invoiceList.region
export const selectIsConfirmed = (state: RootState) =>
  state.invoiceList.isConfirmed
export const selectQuery = (state: RootState) => ({
  from: state.invoiceList.period.startDate,
  to: state.invoiceList.period.endDate,
  orgName: state.invoiceList.search.orgName,
  page: state.invoiceList.paging.pageNumber,
  size: state.invoiceList.paging.pageSize,
  oid: state.invoiceList.oid,
  region: state.invoiceList.region,
})

export const {setIsConfirmed} = customerSlice.actions
